import { useState, useEffect, useCallback } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, MessageSquare, Plus, Send } from 'lucide-react';
import { toast } from 'sonner';
import { STATUS_STYLES, formatDateShort } from './hrUtils';

const CATEGORIES = ['general', 'payroll', 'benefits', 'policy', 'other'];

export const RequestsTab = () => {
  const [requests, setRequests] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ subject: '', category: 'general', message: '' });
  const [replyText, setReplyText] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    hrAPI.listRequests().then((res) => setRequests(res.data)).catch(() => setRequests([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.subject.trim() || !form.message.trim()) { toast.error('Subject and message are required'); return; }
    setSubmitting(true);
    try {
      await hrAPI.createRequest(form);
      toast.success('HR request submitted');
      setShowForm(false);
      setForm({ subject: '', category: 'general', message: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const reply = async (id) => {
    const text = (replyText[id] || '').trim();
    if (!text) return;
    try {
      await hrAPI.replyRequest(id, text);
      setReplyText({ ...replyText, [id]: '' });
      load();
    } catch (err) {
      toast.error('Failed to send reply');
    }
  };

  if (!requests) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  return (
    <div className="space-y-6" data-testid="hr-requests-tab">
      <Card className="bg-white border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-slate-800 text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-cyan-500" /> My HR Requests
          </CardTitle>
          <Button onClick={() => setShowForm(!showForm)} className="bg-cyan-500 hover:bg-cyan-600 rounded-full" size="sm" data-testid="hr-new-request-btn">
            <Plus className="w-4 h-4 mr-1" /> New Request
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {showForm && (
            <div className="p-4 rounded-xl border border-cyan-200 bg-cyan-50/50 space-y-3" data-testid="hr-request-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <Label className="text-slate-700">Subject</Label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-white border-slate-200 mt-1" data-testid="hr-request-subject-input" />
                </div>
                <div>
                  <Label className="text-slate-700">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="bg-white border-slate-200 mt-1 capitalize" data-testid="hr-request-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-slate-700">Message</Label>
                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-white border-slate-200 mt-1" rows={3} data-testid="hr-request-message-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={submit} disabled={submitting} className="bg-cyan-500 hover:bg-cyan-600 rounded-full" data-testid="hr-request-submit-btn">
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Submit
                </Button>
                <Button variant="ghost" onClick={() => setShowForm(false)} className="rounded-full">Cancel</Button>
              </div>
            </div>
          )}

          <div className="space-y-3" data-testid="hr-requests-list">
            {requests.length === 0 && <p className="text-sm text-slate-400 py-4">No HR requests yet. Have a question about payroll, benefits, or policies? Submit one above.</p>}
            {requests.map((r) => (
              <div key={r.id} className="p-4 rounded-xl border border-slate-200 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-slate-800">{r.subject}</p>
                  <Badge variant="secondary" className="capitalize">{r.category}</Badge>
                  <Badge className={STATUS_STYLES[r.status] || ''}>{r.status}</Badge>
                  <span className="text-xs text-slate-400 ml-auto">{formatDateShort(r.created_at)}</span>
                </div>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{r.message}</p>
                {(r.responses || []).map((resp, i) => (
                  <div key={i} className={`p-3 rounded-lg text-sm ${resp.is_admin ? 'bg-cyan-50 border border-cyan-100' : 'bg-slate-50 border border-slate-100'}`}>
                    <p className="text-xs font-medium text-slate-500 mb-1">{resp.by_name}{resp.is_admin ? ' (HR)' : ''} · {formatDateShort(resp.created_at)}</p>
                    <p className="text-slate-700 whitespace-pre-wrap">{resp.message}</p>
                  </div>
                ))}
                {r.status !== 'closed' && (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Write a follow-up..."
                      value={replyText[r.id] || ''}
                      onChange={(e) => setReplyText({ ...replyText, [r.id]: e.target.value })}
                      className="bg-white border-slate-200"
                      data-testid={`hr-request-reply-input-${r.id}`}
                    />
                    <Button size="icon" variant="outline" className="rounded-full shrink-0" onClick={() => reply(r.id)} data-testid={`hr-request-reply-btn-${r.id}`}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
